/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import digitalData from 'digital-data';
import { SERVICES_ONLINE_EXPRESS_BASENAMES } from '../../../constants';
import {
  getTransformedPageName,
  getLivePersonId,
  initDigitalData,
  publishPageLoadAnalytics
} from './servicesOnlineExpress.utils';

// Cache for experiment data and other persistent state
let cacheData = {};

// Use imported constants
const BASENAMES = SERVICES_ONLINE_EXPRESS_BASENAMES;

/**
 * Set digital data for page view
 * @param {object} params - Page view parameters
 */
const setDigitalDataForPageView = ({
  state = '',
  city = '',
  name = '',
  internetId,
  iframe = false,
  pageType = '',
  leadFormPresent = false,
}) => {
  const iframePageNameFormatted = pageType === 'PIP' ? '>brio' : '';
  const stateCityFormatted = state && city ? `>${state}>${city}` : '';
  const nameFormatted = name ? `>${name}` : '';
  const { experimentName, variantId, experimentId } = cacheData;

  // Store experiment data in session storage
  if (experimentId) {
    sessionStorage.setItem('experimentId', experimentId);
    sessionStorage.setItem('experimentName', experimentName);
    sessionStorage.setItem('variantId', variantId);
  }

  // Set page info
  const pageName = `${BASENAMES.PAGE}${iframePageNameFormatted}${stateCityFormatted}${nameFormatted}`;
  digitalData.page.pageInfo.pageName = pageName;
  digitalData.page.category.subCategory1 = `${BASENAMES.CATEGORY}${iframePageNameFormatted}${stateCityFormatted}${nameFormatted}`;
  digitalData.page.category.subCategory2 = `${BASENAMES.SUBCATEGORY}${iframePageNameFormatted}${stateCityFormatted}${nameFormatted}`;
  digitalData.page.form.formName = name;
  digitalData.page.pageInfo.abTest = sessionStorage.getItem('experimentId') 
    ? `${sessionStorage.getItem('experimentName')}|Variant ${sessionStorage.getItem('variantId')}|${sessionStorage.getItem('experimentId')}` 
    : '';

  // Set form and page load event for iframe
  if (iframe) {
    digitalData.page.form = {
      formType: BASENAMES.FORM,
      formName: name,
    };
    digitalData.page.pageInfo.pageLoadEvent = BASENAMES.IFRAME_PAGELOAD;
  }

  // Add product data if internet ID is provided
  if (internetId) {
    digitalData.product.push({
      productId: `productdetails:${internetId}`,
    });
  }

  // Set page load event for lead form
  if (leadFormPresent) {
    digitalData.page.pageInfo.pageLoadEvent = 'installation form start';
  }
};

/**
 * Handle form start event
 * @param {object} output - Event output data
 */
const handleFormStart = ({output = {}}) => {
  try {
    const {
      serviceSlug = '',
      controlSlug = '',
      serviceName = '',
      leadFormPresent = false,
      thdCustomer = null,
    } = output;
    initDigitalData('', thdCustomer);
    setDigitalDataForPageView({
        name: getTransformedPageName(serviceSlug, controlSlug, serviceName),
        leadFormPresent,
      });
    digitalData.page.form.formName = getTransformedPageName(serviceSlug, controlSlug, serviceName);

    publishPageLoadAnalytics();
  } catch (error) {
    console.error('Error handling form start:', error);
  }
};

/**
 * Handle form complete event
 * @param {object} output - Event output data
 */
const handleFormComplete = ({output = {}}) => {
  try {
    const { 
      serviceSlug = '', 
      controlSlug = '',
      serviceName = '',
      orderIdentifier = '', 
      zipcode = '',
      thdCustomer = null,
    } = output;

    initDigitalData('', thdCustomer);
    setDigitalDataForPageView({
      name: getTransformedPageName(serviceSlug, controlSlug, serviceName),
    });
    digitalData.page.pageInfo.pageLoadEvent = 'installation form complete';
    digitalData.page.form.formName = getTransformedPageName(serviceSlug, controlSlug, serviceName);
    digitalData.page.pageInfo.pageName = digitalData.page.pageInfo.pageName.concat('>form complete');
    digitalData.page.form.universalLeadID = orderIdentifier;
    digitalData.page.form.zip = zipcode;
    digitalData.thdAnalyticsEvent.form.livePersonID = getLivePersonId();

    publishPageLoadAnalytics();
  } catch (error) {
    console.error('Error handling form complete:', error);
  }
};

/**
 * Main services online express function that registers all event listeners
 */
export const servicesOnlineExpress = () => {
  // Initialize cache data
  cacheData = {};

  // Register event listeners for services online express events
  LIFE_CYCLE_EVENT_BUS.lifeCycle.on('services-online-express.form-start', handleFormStart);
  LIFE_CYCLE_EVENT_BUS.lifeCycle.on('services-online-express.form-complete', handleFormComplete);
};